import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';

export type RemovalGap = {
  sourceCampaign: string;
  sourceCampaignId: number | null;
  affectedStores: number;
  totalQty: number;
  obligationRows: number;
};

interface RemovalCoverageGapsProps {
  gaps: RemovalGap[];
  cycleId: number | string;
}

export function RemovalCoverageGaps({ gaps, cycleId }: RemovalCoverageGapsProps) {
  const [expanded, setExpanded] = useState(true);
  const queryClient = useQueryClient();

  const materialiseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/api/locked-cycles/${cycleId}/materialise-removals`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Materialisation failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/locked-cycles', String(cycleId), 'signoff-v2-data'] });
    },
  });

  if (gaps.length === 0) return null;

  const totalStores = new Set(gaps.map((g) => g.affectedStores)).size;
  const totalQty = gaps.reduce((sum, g) => sum + g.totalQty, 0);
  const totalStoresAffected = gaps.reduce((max, g) => Math.max(max, g.affectedStores), 0);

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 mb-4">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="font-semibold text-amber-800 text-sm">
            Missing removal tasks — {gaps.length} campaign{gaps.length !== 1 ? 's' : ''} won't appear on operators' phones
          </span>
          <span className="ml-2 rounded-full bg-amber-200 px-2 py-0.5 text-xs font-medium text-amber-800">
            {totalQty} items · up to {Math.max(...gaps.map((g) => g.affectedStores))} stores
          </span>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-amber-600 shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-amber-600 shrink-0" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-amber-200 px-4 pb-4 pt-3">
          <p className="text-xs text-amber-700 mb-3">
            These removals have been calculated but no task has been created for operators yet.
            Click "Fix missing tasks" below to create them — operators will then see them on their phones.
          </p>

          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-amber-200 text-left text-xs font-semibold text-amber-700 uppercase tracking-wide">
                <th className="pb-2 pr-4">Source Campaign</th>
                <th className="pb-2 pr-4 text-right">Stores</th>
                <th className="pb-2 pr-4 text-right">Items</th>
                <th className="pb-2 text-right">Obligation Rows</th>
              </tr>
            </thead>
            <tbody>
              {gaps.map((gap) => (
                <tr
                  key={gap.sourceCampaignId ?? gap.sourceCampaign}
                  className="border-b border-amber-100 last:border-0"
                >
                  <td className="py-1.5 pr-4 font-medium text-gray-800">{gap.sourceCampaign}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums text-gray-700">{gap.affectedStores}</td>
                  <td className="py-1.5 pr-4 text-right tabular-nums text-gray-700">{gap.totalQty}</td>
                  <td className="py-1.5 text-right tabular-nums text-gray-500">{gap.obligationRows}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-3 flex items-center justify-between">
            <p className="text-xs text-amber-600">
              This will create the missing removal tasks so operators can see them.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-100"
              disabled={materialiseMutation.isPending}
              onClick={() => materialiseMutation.mutate()}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${materialiseMutation.isPending ? 'animate-spin' : ''}`} />
              {materialiseMutation.isPending ? 'Fixing…' : 'Fix missing tasks'}
            </Button>
          </div>

          {materialiseMutation.isSuccess && (
            <p className="mt-2 text-xs text-green-700 font-medium">
              ✓ Done — tasks created. This warning should disappear on refresh.
            </p>
          )}
          {materialiseMutation.isError && (
            <p className="mt-2 text-xs text-red-700 font-medium">
              Failed to create tasks. Contact support.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
